import type { ConfiguredAssessmentTypesResponse } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { get } from "@/lib/api/_client";

/**
 * QL-361 — the active product types of a flow's portfolio collection, with
 * their assessment modes (both null for a simple type, QL-541). They are what the `__pa_…` conditional-rule
 * sources are built from: the flow editor's picker lists them, and the advisor
 * wizard needs them to tell "not answered" from "not configured". The shape
 * lives in `@repo/shared/types` and is imported directly by every consumer.
 *
 * `isUnknown` is true while the list is loading, after its fetch failed, or
 * while the query is disabled: the list is then an empty placeholder, which
 * must not read as "every `__pa_…` rule is stale", so condition checks pass
 * `null` instead.
 */

const KEY_SUFFIX = "configured-assessment-types";

const keys = {
  byFlow: (flowId: string | null) => ["flows", flowId, KEY_SUFFIX] as const,
  byTemplate: (templateId: string | null) => ["pdf-templates", templateId, KEY_SUFFIX] as const,
};

export function useFlowConfiguredAssessmentTypes(flowId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: keys.byFlow(flowId ?? null),
    queryFn: () =>
      get<ConfiguredAssessmentTypesResponse>(
        `/internal/flows/${flowId}/configured-assessment-types`,
      ),
    enabled: !!flowId,
  });
  return { configuredAssessmentTypes: data ?? EMPTY, isUnknown: data === undefined };
}

/**
 * The same list for the flow a PDF template is bound to — for the template's
 * conditional-rule pickers.
 */
export function usePdfTemplateConfiguredAssessmentTypes(templateId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: keys.byTemplate(templateId ?? null),
    queryFn: () =>
      get<ConfiguredAssessmentTypesResponse>(
        `/internal/pdf-templates/${templateId}/configured-assessment-types`,
      ),
    enabled: !!templateId,
  });
  return { configuredAssessmentTypes: data ?? EMPTY, isUnknown: data === undefined };
}

/**
 * Marks every cached configured-types list stale — both key families. Called
 * by whatever changes the answer: product type create / update / delete /
 * activate, an assessment mode added or removed, and a flow repointed to
 * another collection. Without it the pickers keep offering a removed type, and
 * the stale-rule warning stays silent, for up to the 60 s staleTime. Matched by
 * suffix because the settings side cannot know which flows or templates use
 * the collection.
 */
export function useInvalidateConfiguredAssessmentTypes() {
  const qc = useQueryClient();
  return useCallback(
    () =>
      qc.invalidateQueries({
        predicate: (query) => query.queryKey[query.queryKey.length - 1] === KEY_SUFFIX,
      }),
    [qc],
  );
}

// Stable reference, so memos keyed on the list do not recompute while loading.
const EMPTY: ConfiguredAssessmentTypesResponse = [];
